import React from 'react';
import { ExternalLink, FilePenLine, FolderPlus, Loader2, Pencil, Share2, Trash2 } from 'lucide-react';

export interface DokumenteRowActionsProps {
  onOpen?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Inhalt in OnlyOffice / Portal-Editor — nicht Umbenennen. */
  onEdit?: () => void;
  onRename?: () => void;
  onShare?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  shareOpen?: boolean;
  onMoveToAkte?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  moveToAkteOpen?: boolean;
  onDelete?: () => void;
  openDisabled?: boolean;
  editDisabled?: boolean;
  renameDisabled?: boolean;
  shareDisabled?: boolean;
  moveToAkteDisabled?: boolean;
  deleteDisabled?: boolean;
  openHint?: string;
  /** Akte-only: Würfel klappt aus diesem Öffnen-Icon. */
  openFlip?: boolean;
  onCloseOpenFlip?: () => void;
  editHint?: string;
  renameHint?: string;
  shareHint?: string;
  moveToAkteHint?: string;
  deleteHint?: string;
  deleteBusy?: boolean;
  /** Während HTML5-Drag: Icons nicht als Hit-Target (sonst stirbt dragover). */
  dragLocked?: boolean;
}

const iconBtn =
  'portal-hell-hover inline-flex items-center justify-center rounded-md p-1.5 text-slate-500 hover:text-slate-100 hover:bg-white/[0.06] transition-colors disabled:opacity-35 disabled:hover:bg-transparent disabled:hover:text-slate-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-violet-400';

export const DokumenteRowActions: React.FC<DokumenteRowActionsProps> = ({
  onOpen,
  onEdit,
  onRename,
  onShare,
  shareOpen,
  onMoveToAkte,
  moveToAkteOpen,
  onDelete,
  openDisabled,
  editDisabled,
  renameDisabled,
  shareDisabled,
  moveToAkteDisabled,
  deleteDisabled,
  openHint,
  openFlip = false,
  onCloseOpenFlip,
  editHint,
  renameHint,
  shareHint,
  moveToAkteHint,
  deleteHint,
  deleteBusy,
  dragLocked,
}) => {
  return (
    <div
      className={`flex items-center gap-0.5 flex-shrink-0 ${dragLocked ? 'pointer-events-none' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {onOpen ? (
      <div
        className="relative h-7 w-7 shrink-0"
        onMouseDown={(event) => {
          if (openFlip) event.stopPropagation();
        }}
      >
        <div
          className={`absolute right-0 top-1/2 z-30 -translate-y-1/2 [perspective:280px] transition-[width,height] duration-500 ${
            openFlip ? 'h-9 w-[17rem]' : 'h-7 w-7'
          }`}
        >
          <div
            data-testid="portal-akte-only-flip"
            className={`portal-akte-only-flip relative h-full w-full transition-transform duration-500 [transform-style:preserve-3d] ${
              openFlip ? 'portal-akte-only-flip-open' : ''
            }`}
            style={{ transform: openFlip ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
          >
            <button
              type="button"
              onClick={(event) => onOpen?.(event)}
              disabled={openDisabled}
              title={openHint || 'Öffnen'}
              aria-label={openHint || 'Öffnen'}
              className={`${iconBtn} absolute right-0 top-0 h-7 w-7 [backface-visibility:hidden]`}
            >
              <ExternalLink className="w-3.5 h-3.5" aria-hidden />
            </button>
            <div
              role="dialog"
              aria-label="Nur in Akten zu öffnen."
              className="portal-akte-only-hint absolute inset-0 flex items-center gap-2 rounded-lg border border-white/[0.12] bg-slate-950 px-2.5 text-slate-100 [backface-visibility:hidden]"
              style={{ transform: 'rotateX(180deg)' }}
            >
              <span className="shrink-0 whitespace-nowrap text-[10px] leading-none">Nur in Akten zu öffnen.</span>
              <button
                type="button"
                className="portal-akte-only-hint-close shrink-0 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white"
                onClick={(event) => {
                  event.stopPropagation();
                  onCloseOpenFlip?.();
                }}
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      </div>
      ) : null}
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          disabled={editDisabled}
          title={editHint || 'Bearbeiten'}
          aria-label={editHint || 'Bearbeiten'}
          data-testid="portal-dokument-edit"
          className={iconBtn}
        >
          <FilePenLine className="w-3.5 h-3.5" aria-hidden />
        </button>
      ) : null}
      {onRename ? (
        <button
          type="button"
          onClick={onRename}
          disabled={renameDisabled}
          title={renameHint || 'Umbenennen'}
          aria-label={renameHint || 'Umbenennen'}
          className={iconBtn}
        >
          <Pencil className="w-3.5 h-3.5" aria-hidden />
        </button>
      ) : null}
      {onShare ? (
        <button
          type="button"
          onClick={onShare}
          disabled={shareDisabled}
          title={shareHint || 'Teilen'}
          aria-label={shareHint || 'Teilen'}
          aria-expanded={shareOpen}
          aria-haspopup="dialog"
          className={`${iconBtn}${shareOpen ? ' text-violet-200 bg-violet-500/15' : ''}`}
        >
          <Share2 className="w-3.5 h-3.5" aria-hidden />
        </button>
      ) : null}
      {onMoveToAkte ? (
        <button
          type="button"
          onClick={onMoveToAkte}
          disabled={moveToAkteDisabled}
          title={moveToAkteHint || 'In Akte kopieren oder einfügen…'}
          aria-label={moveToAkteHint || 'In Akte kopieren oder einfügen…'}
          aria-expanded={moveToAkteOpen}
          aria-haspopup="dialog"
          className={`${iconBtn}${moveToAkteOpen ? ' text-violet-200 bg-violet-500/15' : ''}`}
        >
          <FolderPlus className="w-3.5 h-3.5" aria-hidden />
        </button>
      ) : null}
      <button
        type="button"
        onClick={onDelete}
        disabled={deleteDisabled || !onDelete || deleteBusy}
        title={deleteHint || 'Löschen'}
        aria-label={deleteHint || 'Löschen'}
        className={`${iconBtn} hover:text-red-300`}
      >
        {deleteBusy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
        ) : (
          <Trash2 className="w-3.5 h-3.5" aria-hidden />
        )}
      </button>
    </div>
  );
};
